import { useState } from "react";
import { Link } from "react-router-dom";
import { Page, useCurrent, Grid } from "./common";
import { PageHead, Stat } from "../components/ui";
import { Icon } from "../components/Icons";
import { useCan, useSession } from "../lib/auth";
import { actions, money, STUDIO } from "../lib/store";
import { billingTotals } from "../lib/billing";
import { fmtDate } from "../lib/format";
import { openInvoices, ledgerRows, claimRows, ReceiptsCard, UpcomingCard } from "../components/PaymentLedger";
import { RecordPaymentModal } from "./BillingSchedule";
import { SendInvoiceButton } from "../components/SendInvoice";
import { ShareStanding } from "../components/ShareStanding";
import { downloadInvoicePdf } from "../lib/pdf";

/* The accountant's room: reconcile what came in, see what is still owed, and
   understand how the money is meant to arrive — without touching the design. */
export default function Ledger() {
  const p = useCurrent();
  const canEdit = useCan("payments");
  const session = useSession();
  const [payFor, setPayFor] = useState<string | null>(null);
  if (!p) return null;
  const who = session?.name ?? STUDIO.principal;

  const t = billingTotals(p);
  const cur = p.billing?.currency ?? p.currency;
  const open = openInvoices(p);
  const receipts = ledgerRows(p).length;
  const claims = claimRows(p);
  const owed = open.reduce((s, x) => s + x.owed, 0);
  const b = p.billing;

  return (
    <Page>
      <PageHead kicker={`${p.name} · Finance`} title="Payments ledger">
        <Link className="btn ghost sm" to={`/studio/${p.id}/billing`}>Billing & schedule <Icon.arrow width={14} /></Link>
      </PageHead>
      {/* the standing, out of the door — WhatsApp, email, or the link and summary to paste anywhere */}
      <div className="card reveal row between wrap" style={{ padding: "11px 18px", marginBottom: 16, gap: 10 }}>
        <span style={{ minWidth: 0 }}>
          <span className="kicker">Share the current standing</span>
          <span className="dim" style={{ display: "block", fontSize: 11.5, marginTop: 2 }}>Contract value, invoiced, received, balance due, each open invoice, and the portal link where the client pays.</span>
        </span>
        {canEdit && <ShareStanding p={p} by={who} />}
      </div>

      {/* Invoices — and the claims against them — exist whether or not a payment
          structure has been set, so the ledger can never be gated on one. */}
      {!b && p.invoices.length === 0 ? (
        <div className="card pad reveal" style={{ maxWidth: 560 }}>
          <div className="kicker">No payment structure yet</div>
          <p className="muted" style={{ fontSize: 14.5, marginTop: 8, lineHeight: 1.6 }}>
            Set the agreed structure on Billing — scan the signed contract or build it from the fee
            stages — and every invoice and receipt lands here.
          </p>
          <Link className="btn accent" style={{ marginTop: 14 }} to={`/studio/${p.id}/billing`}>Open Billing <Icon.arrow width={15} /></Link>
        </div>
      ) : (
        <>
          {/* the five states money can be in, in the order it moves through them */}
          {b && (
            <>
              <Grid cols="repeat(5,1fr)" style={{ marginBottom: 10 }}>
                <Stat label="Contract value" sub="Agreed fee">{money(t?.billable ?? 0, cur)}</Stat>
                <Stat label="Invoiced" sub="Billed to date">{money(t?.invoiced ?? 0, cur)}</Stat>
                <Stat label="Received" sub={`${receipts} receipt${receipts === 1 ? "" : "s"}`}>{money(t?.paid ?? 0, cur)}</Stat>
                <Stat label="Balance due" accent sub={b.tax?.enabled ? "Fee, ex-VAT" : "Invoiced, unpaid"}>{money(t?.dueNow ?? owed, cur)}</Stat>
                <Stat label="Still to bill" sub="Not yet invoiced">{money(t?.remainingToBill ?? 0, cur)}</Stat>
              </Grid>

              {/* tax is a pass-through, kept out of the fee figures above */}
              {t && t.taxInvoiced > 0 && (
                <div className="card reveal row between wrap" style={{ padding: "11px 18px", marginBottom: 14, gap: 10, background: "var(--paper-2)" }}>
                  <span className="row" style={{ gap: 8, fontSize: 12.5 }}>
                    <Icon.check width={14} style={{ color: "var(--ink-3)" }} />
                    <span><b>{b.tax?.label || "VAT"}</b> is charged on top and tracked separately — it is not part of the fee invoiced or paid above.</span>
                  </span>
                  <span className="row" style={{ gap: 16, fontSize: 12.5 }}>
                    <span><span className="dim">Tax invoiced</span> <span className="mono">{money(t.taxInvoiced, cur)}</span></span>
                    <span><span className="dim">Tax paid</span> <span className="mono" style={{ color: "var(--ok)" }}>{money(t.taxPaid, cur)}</span></span>
                    {t.taxDue > 0 && <span><span className="dim">Tax due</span> <span className="mono" style={{ color: "var(--accent-ink)" }}>{money(t.taxDue, cur)}</span></span>}
                  </span>
                </div>
              )}

              <p className="dim reveal" style={{ fontSize: 12, marginBottom: 24, maxWidth: 760, lineHeight: 1.6 }}>
                Money moves left to right: the contract is <b>earned</b> as work is certified, <b>invoiced</b> when
                that certificate is billed, and <b>received</b> when it lands. Only what has been invoiced and not
                paid is due — the rest is future work.
                {t && t.deferred > 0 && <> {money(t.deferred, cur)} is <b>earned but deferred</b>{b.deferred ? `, payable ${b.deferred.trigger}` : ""}.</>}
              </p>
            </>
          )}
          {!b && (
            <p className="dim reveal" style={{ fontSize: 12.5, marginBottom: 20, maxWidth: 700, lineHeight: 1.6 }}>
              No payment structure is set on this project — <Link to={`/studio/${p.id}/billing`}>add one on Billing</Link> for
              contract totals. The invoices already issued, and anything the client has paid against them, are below.
            </p>
          )}

          {/* claims first — money the client says they sent, still unverified */}
          {claims.length > 0 && (
            <div className="card reveal" style={{ overflow: "hidden", marginBottom: 18, borderColor: "var(--warn)" }}>
              <div className="row between" style={{ padding: "14px 18px", borderBottom: "1px solid var(--line)", background: "color-mix(in srgb, var(--warn) 8%, transparent)" }}>
                <span className="kicker">Awaiting your confirmation</span>
                <span className="dim" style={{ fontSize: 12 }}>{money(claims.reduce((s, c) => s + c.pay.amount, 0), cur)} claimed</span>
              </div>
              <div className="dim" style={{ padding: "10px 18px 2px", fontSize: 11.5 }}>
                The client says these have been sent. Check the account first — nothing here counts as received until you confirm it.
              </div>
              {claims.map((c) => (
                <div key={c.pay.id} className="row between wrap" style={{ padding: "12px 18px", borderTop: "1px solid var(--line)", gap: 12 }}>
                  <span style={{ minWidth: 0 }}>
                    <span style={{ fontSize: 14, fontWeight: 500 }}>{money(c.pay.amount, cur)} — {c.what}</span>
                    <span className="dim mono" style={{ fontSize: 11, display: "block", marginTop: 3 }}>
                      {c.invoiceRef} · {fmtDate(c.pay.on)}
                      {c.pay.method ? ` · ${c.pay.method}` : ""}
                      {c.pay.reference ? ` · ref ${c.pay.reference}` : ""}
                      {c.pay.by ? ` · from ${c.pay.by}` : ""}
                    </span>
                    {c.pay.note && <span className="dim" style={{ fontSize: 11.5, display: "block", marginTop: 2 }}>{c.pay.note}</span>}
                  </span>
                  <span className="row wrap" style={{ gap: 8, flexShrink: 0 }}>
                    {c.pay.proof && (
                      <a className="btn ghost sm" href={c.pay.proof.url} target="_blank" rel="noreferrer">
                        <Icon.download width={13} />View proof
                      </a>
                    )}
                    {canEdit && (
                      <button className="btn ghost sm" onClick={() => actions.rejectInvoicePayment(p.id, c.invoiceId, c.pay.id, who)}>
                        Not received
                      </button>
                    )}
                    {canEdit && (
                      <button className="btn accent sm" onClick={() => actions.confirmInvoicePayment(p.id, c.invoiceId, c.pay.id, who)}>
                        <Icon.check width={13} />Confirm received
                      </button>
                    )}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* record a payment — the point of the room */}
          <div className="card reveal" style={{ overflow: "hidden", marginBottom: 18 }}>
            <div className="row between" style={{ padding: "14px 18px", borderBottom: "1px solid var(--line)" }}>
              <span className="kicker">Open invoices</span>
              <span className="dim" style={{ fontSize: 12 }}>{money(owed, cur)} outstanding</span>
            </div>
            {open.length === 0
              ? <div className="dim" style={{ padding: 18, fontSize: 13 }}>Nothing outstanding — every invoice issued has been settled.</div>
              : open.map(({ inv, paid, owed: o, claimed }, i) => (
                <div key={inv.id} className="row between wrap" style={{ padding: "13px 18px", borderTop: i ? "1px solid var(--line)" : "none", gap: 12 }}>
                  <span style={{ minWidth: 0 }}>
                    <span style={{ fontSize: 14, fontWeight: 500 }}>{inv.title}</span>
                    <span className="dim mono" style={{ fontSize: 11, display: "block", marginTop: 3 }}>
                      {inv.ref} · issued {fmtDate(inv.issuedOn)} · due {fmtDate(inv.dueOn)}
                      {inv.taxRate ? ` · incl. ${(inv.taxRate * 100).toFixed(inv.taxRate * 100 % 1 ? 1 : 0)}% ${inv.taxLabel || "VAT"}` : ""}
                      {paid > 0 ? ` · ${money(paid, cur)} of ${money(inv.amount, cur)} received` : ""}
                    </span>
                    {claimed > 0 && <span className="badge warn" style={{ fontSize: 9, marginTop: 5 }}>{money(claimed, cur)} claimed, unconfirmed</span>}
                  </span>
                  <span className="col" style={{ gap: 6, alignItems: "flex-end", flexShrink: 0 }}>
                    <span className="row" style={{ gap: 12 }}>
                      <span className="serif" style={{ fontSize: 19 }}>{money(o, cur)}</span>
                      {canEdit && (
                        <button className="btn accent sm" onClick={() => setPayFor(inv.id)}>
                          <Icon.check width={13} />Record payment
                        </button>
                      )}
                    </span>
                    <span className="row wrap" style={{ gap: 6, justifyContent: "flex-end" }}>
                      <button className="btn ghost sm" onClick={() => void downloadInvoicePdf(p, inv)}><Icon.download width={13} />Invoice PDF</button>
                      {canEdit && <SendInvoiceButton p={p} inv={inv} by={who} />}
                    </span>
                  </span>
                </div>
              ))}
          </div>

          <div className="col" style={{ gap: 18 }}>
            <ReceiptsCard p={p} mode="studio" />
            <UpcomingCard p={p} />
          </div>
        </>
      )}

      {payFor && <RecordPaymentModal p={p} invoiceId={payFor} onClose={() => setPayFor(null)} />}
    </Page>
  );
}
